import { useState, useEffect } from "react";
import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { useBoundValue } from "../hooks/use-bound-value";
import {
  claimNativeInputSyncDispatch,
  useNativeInputSync,
} from "../hooks/use-native-input-sync";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { CalendarIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { resolveIcon } from "./resolve-icon";
import {
  isSourceDerivedElement,
  readSourceFrame,
  sourceFillBoxStyle,
} from "./source-derived-rendering";

/** Extract last segment from a bind path. E.g., "#{form.email}" -> "email" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined;
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "");
  const segments = stripped.split(".");
  return segments[segments.length - 1];
}

function DateInputPopover({
  element,
  boundValue,
  placeholder,
  bind,
}: {
  element: { id: string; className?: string };
  boundValue: string;
  placeholder: string;
  bind?: string;
}) {
  const { dispatchEvent, itemContext } = useRenderContext();
  const [open, setOpen] = useState(false);
  const [localDate, setLocalDate] = useState<Date | undefined>(undefined);

  // Sync local state from bound state (date format conversion -- separate concern from state sync)
  useEffect(() => {
    if (boundValue) {
      const parsed = parse(boundValue, "yyyy-MM-dd", new Date());
      if (isValid(parsed)) {
        setLocalDate(parsed);
      }
    } else {
      setLocalDate(undefined);
    }
  }, [boundValue]);

  const handleSelect = (date: Date | undefined) => {
    setLocalDate(date);
    const value = date ? format(date, "yyyy-MM-dd") : "";
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* base-ui uses `render` for composition; the original `asChild` pattern
          was a Radix leftover. Button is rendered as-is with merged trigger props. */}
      <PopoverTrigger
        data-empty={!localDate}
        data-testid={`input-${element.id}`}
        data-action="input"
        data-field-name={extractFieldName(bind)}
        data-field-type="date"
        data-atom-type="input"
        render={
          <Button
            id={element.id}
            variant="outline"
            className={cn(
              "justify-start text-left font-normal data-[empty=true]:text-muted-foreground",
              element.className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
            {localDate ? format(localDate, "yyyy-MM-dd") : placeholder}
          </Button>
        }
      />
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          mode="single"
          selected={localDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function sourceInputShellTop(element: RendererProps["element"]): number {
  const frame = readSourceFrame(element);
  const contract = asRecord(
    (element as unknown as Record<string, unknown>).componentContract,
  );
  const slots = Array.isArray(contract?.internalSlots)
    ? contract.internalSlots.filter((slot): slot is Record<string, unknown> =>
        Boolean(asRecord(slot)),
      )
    : [];
  const inputShell = slots.find((slot) => slot.slotName === "input-shell");
  const shellFrame = asRecord(inputShell?.frame);
  const shellTop =
    typeof shellFrame?.y === "number" && frame ? shellFrame.y - frame.y : null;
  if (shellTop != null && shellTop >= 0 && shellTop < (frame?.height ?? 0))
    return Math.round(shellTop);
  if (frame?.height && frame.height <= 56)
    return Math.max(16, Math.round(frame.height / 2 - 10));
  return 38;
}

function isEditableSourceInputShell(
  element: RendererProps["element"],
): boolean {
  const contract = asRecord(
    (element as unknown as Record<string, unknown>).componentContract,
  );
  return (
    element.type === "input" &&
    isSourceDerivedElement(element) &&
    contract?.componentAssemblyKind === "editable-input-shell"
  );
}

function isTrue(value: unknown): boolean {
  return value === true || value === "true";
}

function readNonNegativeInteger(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : undefined;
}

export function InputRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext, designTokens } =
    useRenderContext();
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("input");
  const resolvedVariant =
    (element as { variant?: string }).variant ??
    atomDefaults.defaultVariant ??
    "default";
  const resolvedSize =
    (element as { size?: string }).size ??
    atomDefaults.defaultSize ??
    "default";
  const rawTone = elementRecordValue(element, "tone");
  const tone =
    typeof rawTone === "string"
      ? String(resolveSymbol(rawTone) ?? rawTone).toLowerCase()
      : "default";
  const toneClassName = inputToneClassName(tone);
  const variantClassName = inputVariantClassName(resolvedVariant);
  const sizeClassName = inputSizeClassName(resolvedSize);
  const hintRaw = (element as unknown as { hint?: unknown }).hint;
  const hint =
    hintRaw != null
      ? String(resolveSymbol(String(hintRaw)) ?? hintRaw)
      : undefined;
  const placeholderRaw = element.placeholder ?? hintRaw;
  const placeholder = placeholderRaw
    ? String(resolveSymbol(String(placeholderRaw)) ?? placeholderRaw)
    : undefined;
  const elementRecord = element as unknown as Record<string, unknown>;
  const obscureText = isTrue(elementRecord.obscureText);
  const intOnly = isTrue(elementRecord.intOnly);
  const required = isTrue(element.required);
  const disabled = isTrue(element.disabled);
  const readOnlyValue = elementRecord.readOnly ?? elementRecord.readonly;
  const readOnly = isTrue(readOnlyValue);
  const maxLength = readNonNegativeInteger(
    elementRecord.maxLength ?? elementRecord.maxlength,
  );
  const declaredType = String(element.inputType ?? "text");
  const isDateType =
    !obscureText && ["date", "month", "datetime-local"].includes(declaredType);
  const inputType = obscureText
    ? "password"
    : isDateType
      ? "text"
      : declaredType;
  const pattern =
    typeof element.pattern === "string" && element.pattern
      ? element.pattern
      : intOnly
        ? "[0-9]*"
        : undefined;
  const min = element.min as number | undefined;
  const max = element.max as number | undefined;
  const step = element.step as number | undefined;
  const label = element.label
    ? String(resolveSymbol(element.label) ?? element.label)
    : undefined;
  const rawIcon = typeof element.icon === "string" ? element.icon : "";
  const iconName = rawIcon.includes("#{")
    ? String(resolveSymbol(rawIcon) ?? "")
    : rawIcon;
  const InputIcon = iconName ? resolveIcon(iconName) : undefined;

  const normalizeValue = (value: string) =>
    intOnly ? value.replace(/[^0-9]/g, "") : value;

  // State binding via useBoundValue (1-cycle sync, no useEffect delay)
  const [boundValue, setBoundValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  );

  // Native DOM input event listener -- catches Playwright page.fill()/page.type()
  // which may not trigger React's synthetic onChange.
  const nativeRef = useNativeInputSync((value) => {
    const normalizedValue = normalizeValue(value);
    setBoundValue(normalizedValue);
    dispatchEvent(element.id, "change", {
      ...(itemContext ?? {}),
      value: normalizedValue,
    });
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = normalizeValue(e.target.value);
    if (value !== e.currentTarget.value) e.currentTarget.value = value;
    setBoundValue(value);
    if (claimNativeInputSyncDispatch(e.nativeEvent)) {
      dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Standard DOM "keydown" event dispatch. Blocks bind "<id>:keydown" and
    // branch on #{event.key === 'Enter'} / 'Escape' / 'Tab' inside the flow.
    // No hardcoded ':enter' / ':escape' event names — keep the runtime
    // dispatch aligned with the event-types ontology.
    dispatchEvent(element.id, "keydown", {
      ...(itemContext ?? {}),
      key: e.key,
      metaKey: e.metaKey,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      value: e.currentTarget.value,
    });
  };
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    dispatchEvent(element.id, "focus", {
      ...(itemContext ?? {}),
      value: e.currentTarget.value,
    });
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    dispatchEvent(element.id, "blur", {
      ...(itemContext ?? {}),
      value: e.currentTarget.value,
    });
  };
  const datePlaceholder = isDateType
    ? placeholder || (declaredType === "month" ? "YYYY-MM" : "YYYY-MM-DD")
    : placeholder;

  const inputProps = {
    id: element.id,
    name: element.name ?? extractFieldName(element.bind),
    "aria-label": element.ariaLabel
      ? String(resolveSymbol(element.ariaLabel) ?? element.ariaLabel)
      : undefined,
    type: inputType,
    autoComplete: inputType === "password"
      ? "current-password"
      : declaredType === "email" ? "username" : declaredType === "search" ? "off" : undefined,
    placeholder: datePlaceholder,
    required: required || undefined,
    disabled: disabled || undefined,
    readOnly: readOnly || undefined,
    pattern: pattern || undefined,
    inputMode: intOnly ? ("numeric" as const) : undefined,
    min: min,
    max: max,
    step: step,
    maxLength,
    ...(element.bind !== undefined
      ? { value: boundValue }
      : (element as any).defaultValue !== undefined
        ? {
            defaultValue: String(
              resolveSymbol((element as any).defaultValue) ?? "",
            ),
          }
        : (element as any).value !== undefined
          ? {
              defaultValue: String(
                resolveSymbol((element as any).value) ?? (element as any).value,
              ),
            }
          : {}),
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    "data-testid": `input-${element.id}`,
    "data-action": "input" as const,
    "data-field-name": extractFieldName(element.bind),
    "data-field-type": declaredType,
    "data-atom-type": "input" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
    "data-tone": tone,
    "data-hint": hint || undefined,
  };
  const fieldDataAttrs = {
    "data-cogo-input-field": "true" as const,
    "data-tone": tone,
    "data-hint": hint || undefined,
  };
  const toneInlineStyle = inputToneInlineStyle(tone, designTokens?.colors);
  const inlineStyle = {
    boxSizing: "border-box",
    height: inputSizeHeight(resolvedSize),
    minHeight: inputSizeHeight(resolvedSize),
    ...toneInlineStyle,
    ...(element.style && typeof element.style === "object"
      ? (element.style as React.CSSProperties)
      : {}),
  } satisfies React.CSSProperties;

  if (isEditableSourceInputShell(element)) {
    const displayedValue = boundValue ? String(boundValue) : "";
    const displayText = displayedValue || datePlaceholder || "";
    const isPlaceholderVisible = !displayedValue && Boolean(datePlaceholder);
    const fieldTextTop = sourceInputShellTop(element);
    const sourceStyle = sourceFillBoxStyle(element, designTokens);
    const shellStyle: React.CSSProperties = {
      ...sourceStyle,
      position: "relative",
      boxSizing: "border-box",
      display: "block",
      overflow: "hidden",
      borderStyle:
        sourceStyle.borderColor != null ? "solid" : sourceStyle.borderStyle,
      borderWidth:
        sourceStyle.borderColor != null
          ? (sourceStyle.borderWidth ?? 1)
          : sourceStyle.borderWidth,
    };
    const hiddenNativeInputStyle: React.CSSProperties = {
      position: "absolute",
      inset: 0,
      zIndex: 2,
      width: "100%",
      height: "100%",
      border: 0,
      background: "transparent",
      color: "transparent",
      caretColor: "#0c1723",
      outline: "none",
      padding: 0,
      opacity: disabled ? 0.45 : 1,
    };

    const sourceInput = (
      <div
        data-cogo-source-input-shell="true"
        data-cogo-source-input-kind="editable-input-shell"
        data-atom-type="input"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        style={shellStyle}
      >
        <input
          ref={nativeRef}
          className="cogo-source-input-hit-target"
          style={hiddenNativeInputStyle}
          {...inputProps}
          aria-label={
            inputProps["aria-label"] ??
            datePlaceholder ??
            extractFieldName(element.bind)
          }
        />
        {displayText ? (
          <span
            data-cogo-source-input-slot={
              displayedValue ? "value" : "placeholder"
            }
            data-cogo-source-input-placeholder={
              isPlaceholderVisible ? "true" : undefined
            }
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 20,
              right: 20,
              top: fieldTextTop,
              zIndex: 1,
              display: "block",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre-line",
              pointerEvents: "none",
              color: isPlaceholderVisible ? "#0c172399" : "#0c1723",
              fontFamily:
                '"Pretendard", -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", "Segoe UI", sans-serif',
              fontSize: 17,
              fontWeight: 500,
              lineHeight: "20.4px",
            }}
          >
            {displayText}
          </span>
        ) : null}
      </div>
    );
    return label ? (
      <div className="grid gap-2" {...fieldDataAttrs}>
        <Label htmlFor={element.id}>{label}</Label>
        {sourceInput}
      </div>
    ) : (
      sourceInput
    );
  }

  // Date input: shadcn Calendar in Popover (separate component for hooks)
  if (isDateType && declaredType === "date") {
    const dateInput = (
      <DateInputPopover
        element={element}
        boundValue={boundValue ?? ""}
        placeholder={datePlaceholder ?? "YYYY-MM-DD"}
        bind={element.bind}
      />
    );
    return label ? (
      <div className="grid gap-2" {...fieldDataAttrs}>
        <Label htmlFor={element.id}>{label}</Label>
        {dateInput}
      </div>
    ) : (
      dateInput
    );
  }

  // Render any known ontology icon as a leading input affordance. Unknown
  // names remain fail-closed: no icon metadata and no reserved icon padding.
  if (InputIcon) {
    const iconInput = (
      <div
        className="relative w-full"
        data-atom-type="input"
        data-icon={iconName}
      >
        <InputIcon
          aria-hidden="true"
          data-cogo-input-icon={iconName}
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
        />
        <Input
          ref={nativeRef}
          data-icon={iconName}
          className={cn(
            "!pl-9",
            "read-only:cursor-default read-only:bg-muted/40",
            readOnly && "cursor-default bg-muted/60 text-muted-foreground",
            variantClassName,
            sizeClassName,
            toneClassName,
            element.className,
          )}
          style={inlineStyle}
          {...inputProps}
        />
      </div>
    );
    return label ? (
      <div className="grid gap-2" {...fieldDataAttrs}>
        <Label htmlFor={element.id}>{label}</Label>
        {iconInput}
      </div>
    ) : (
      iconInput
    );
  }

  const input = (
    <Input
      ref={nativeRef}
      className={cn(
        "read-only:cursor-default read-only:bg-muted/40",
        readOnly && "cursor-default bg-muted/60 text-muted-foreground",
        variantClassName,
        sizeClassName,
        toneClassName,
        element.className,
      )}
      style={inlineStyle}
      {...inputProps}
    />
  );
  return label ? (
    <div className="grid gap-2" {...fieldDataAttrs}>
      <Label htmlFor={element.id}>{label}</Label>
      {input}
    </div>
  ) : (
    input
  );
}

function elementRecordValue(
  element: RendererProps["element"],
  property: string,
): unknown {
  return (element as unknown as Record<string, unknown>)[property];
}

function inputToneClassName(tone: string): string | undefined {
  switch (tone) {
    case "destructive":
    case "danger":
    case "error":
      return "border border-destructive text-destructive focus-visible:ring-destructive/30";
    case "primary":
      return "border border-primary focus-visible:ring-primary/30";
    case "secondary":
      return "border border-secondary focus-visible:ring-secondary/30";
    case "muted":
    case "soft":
      return "border border-muted bg-muted/50";
    default:
      return undefined;
  }
}

function inputToneInlineStyle(
  tone: string,
  colors: Record<string, string> | undefined,
): React.CSSProperties | undefined {
  if (!["destructive", "danger", "error"].includes(tone)) return undefined;

  // Keep the semantic Tailwind classes above, but also materialize the tone on
  // the native control. Renderer consumers may supply only the runtime token
  // variables (without a Tailwind utility bundle), so class names alone are
  // not sufficient evidence that the authored destructive tone is visible.
  const destructiveColor = colors?.destructive ?? "var(--destructive, #dc2626)";
  return {
    borderColor: destructiveColor,
    borderStyle: "solid",
    borderWidth: 1,
    color: destructiveColor,
    caretColor: destructiveColor,
  };
}

function inputVariantClassName(variant: string): string {
  return variant === "outline"
    ? "border border-input bg-background shadow-xs"
    : "border border-transparent bg-muted/40";
}

function inputSizeClassName(size: string): string {
  switch (size) {
    case "sm":
      return "h-8 px-2.5 text-sm";
    case "lg":
      return "h-12 px-4 text-base";
    case "md":
    case "default":
    default:
      return "h-10 px-3";
  }
}

function inputSizeHeight(size: string): number {
  switch (size) {
    case "sm":
      return 32;
    case "lg":
      return 48;
    case "md":
    case "default":
    default:
      return 40;
  }
}
